import { useSchedulerRunHistory } from "@platform/hooks";
import { PageHeader } from "../../components/shared/PageHeader";
import { History } from "lucide-react";

const STATUS_STYLE: Record<string, string> = {
  running: "bg-blue-50 text-blue-700 border-blue-100",
  success: "bg-emerald-50 text-emerald-700 border-emerald-100",
  failed: "bg-red-50 text-red-700 border-red-100",
};

const JOB_TYPE_LABEL: Record<string, string> = { etl: "ETL", report_generation: "Report generation" };

function formatDate(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : "—";
}

export function PlatformSchedulerHistoryPage() {
  const { data: runs, isLoading } = useSchedulerRunHistory();

  return (
    <div className="space-y-6">
      <PageHeader
        title="Scheduler history"
        subtitle="Every run across every job — not scoped to one job at a time, unlike each job card's own recent-runs list."
      />

      {isLoading && <p className="text-sm text-ds-text-muted">Loading…</p>}

      {!isLoading && (!runs || runs.length === 0) && (
        <div className="bg-white rounded-[12px] border border-ds-border p-10 text-center">
          <History className="w-8 h-8 text-ds-text-muted mx-auto mb-2" />
          <p className="text-sm text-ds-text-secondary">No runs yet.</p>
        </div>
      )}

      {!isLoading && runs && runs.length > 0 && (
        <div className="bg-white rounded-[12px] border border-ds-border overflow-hidden divide-y divide-ds-border">
          {runs.map((run) => (
            <div key={run.id} className="flex items-center gap-3 px-5 py-3.5">
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border shrink-0 ${STATUS_STYLE[run.status]}`}>
                {run.status}
              </span>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium text-ds-text truncate">{run.jobName}</p>
                  <span className="text-[11px] font-medium uppercase tracking-wide px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-600 border border-indigo-100 shrink-0">
                    {JOB_TYPE_LABEL[run.jobType]}
                  </span>
                </div>
                {run.resultMessage && <p className="text-xs text-ds-text-muted mt-0.5 truncate">{run.resultMessage}</p>}
              </div>
              <div className="text-right shrink-0">
                <p className="text-xs text-ds-text-secondary">{run.triggeredBy}</p>
                <p className="text-xs text-ds-text-muted">{formatDate(run.startedAt)}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
